import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);

export default function PackageCard({ pkg }) {
  const { t, ml } = useLang();
  const features = ml(pkg.features);
  const featureList = Array.isArray(features) ? features : [];
  const popular = Boolean(pkg.popular);
  const speed = pkg.speed || 0;

  return (
    <div
      data-testid={`package-card-${speed}`}
      className={`relative flex flex-col rounded-2xl border p-8 transition-all duration-300 ${
        popular
          ? "border-[#2DD4BF]/40 bg-[#062B2B] text-white shadow-2xl shadow-[#0D9488]/10 lg:scale-[1.03]"
          : "border-zinc-200 bg-white text-[#062B2B] shadow-sm hover:border-[#2DD4BF] hover:shadow-xl hover:shadow-[#0D9488]/5"
      }`}
    >
      {popular && (
        <span className="absolute -top-3.5 start-1/2 -translate-x-1/2 rounded-lg bg-[#2DD4BF] px-4 py-1 text-xs font-bold uppercase tracking-[0.15em] text-[#042E2E] shadow-lg">
          {t("packages.popular")}
        </span>
      )}

      <div className={`flex items-center gap-3 border-b pb-5 ${popular ? "border-white/10" : "border-zinc-100"}`}>
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${popular ? "text-[#2DD4BF]" : "text-[#0D9488]"}`}>
            {pkg.name}
          </p>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="font-display text-5xl font-extrabold tracking-tighter">{speed}</span>
            <span className={`text-base font-bold ${popular ? "text-[#2DD4BF]" : "text-[#0D9488]"}`}>{t("packages.mbps")}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-display text-3xl font-bold tracking-tight">₺{formatPrice(pkg.price)}</span>
        <span className={popular ? "text-zinc-500" : "text-gray-400"}>{t("packages.perMonth")}</span>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {featureList.map((feature, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
              popular ? "bg-[#2DD4BF] text-[#042E2E]" : "bg-[#F0FDFA] text-[#0D9488]"
            }`}>
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className={popular ? "text-zinc-300" : "text-gray-600"}>{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/tarifeler/${pkg.id}`}
        data-testid={`package-select-${speed}`}
        className={`mt-8 flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-bold transition-all ${
          popular
            ? "bg-[#2DD4BF] text-[#042E2E] hover:bg-[#14B8A6] hover:shadow-lg hover:shadow-[#2DD4BF]/20"
            : "border-2 border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-white"
        }`}
      >
        {t("packages.select")}
        <ArrowRight className="h-4 w-4 rtl:rotate-180" />
      </Link>
    </div>
  );
}
